import { defineAsyncComponent, markRaw } from 'vue';

export function getNestedSlices(node: any): any[] {
    if (!node || !node.data) return [];
    // TODO: this is more complicated than it should be.
    // make s-all-products use the same format as wrapper, tabs, side-menu
    const nested = node.name === 's-all-products'
        ? (node.data.slicesData || []).flatMap((x: any) => x?.slices || [])
        : (node.data.slices || []);

    return Array.isArray(nested) ? nested.filter(Boolean) : [];
}

// Recursive routine for dynamic slices resolution (including `.data.slices[*].name`).
// Used for dynamic slices resolution in pages view components.
export function resolvePageBodySlices(pageBody: any[]): string[] {
    const names = new Set<string>();

    // Only interested in dynamic slices, starting with `s-`
    const maybeAdd = (name: any) => {
        if (name?.startsWith('s-')) {
            names.add(name);
        }
    };

    const resolveRecursive = (node: any) => {
        maybeAdd(node.name);
        getNestedSlices(node).forEach(resolveRecursive);
    };

    pageBody.forEach(resolveRecursive);

    return Array.from(names);
}

export function defaultSectionResolver(name: string): string {
    if (name.startsWith('s-developer')) return 'developer';
    if (name.startsWith('s-trueimage')) return 'trueimage';
    if (name.startsWith('s-acronis-scs')) return 'scs';
    return 'pages';
}

/**
 * Register slice components dynamically.
 *
 * @param options.metaModules Optional: META_MODULES from import.meta.glob
 * @param options.pageBody The `page.body` data
 * @param options.componentRegistrar A function called for each name/component
 * @param options.sectionResolver A function to resolve the section for a given name (defaults to 'pages')
 */
export function registerSlices({ metaModules,
    pageBody,
    componentRegistrar,
    sectionResolver = defaultSectionResolver }: {
    metaModules?: Record<string, () => Promise<any>>;
    pageBody: any[];
    componentRegistrar: (name: string, component: any) => void;
    sectionResolver?: (fullname: string) => string;
}) {
    const names = resolvePageBodySlices(pageBody);

    names.forEach((fullname) => {
        const sliceName = fullname.replace(/^s-/, '');
        const section = sectionResolver(fullname);

        const path = `/src/core/slices/${section}/${sliceName}/${sliceName}.vue`;
        const loader = metaModules?.[path] || (() => import(`@core/slices/${section}/${sliceName}/${sliceName}.vue`));

        try {
            const asyncComponent = markRaw(defineAsyncComponent(loader));
            componentRegistrar(fullname, asyncComponent);
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error(`[registerSlices] Failed to register slice: "${fullname}"`, err);
        }
    });
}
